<script setup lang="ts">
// 앱 설정: 화면·보안·위장·데이터·도움. 위장 설정도 여기로 합쳤다.
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore } from '../stores/ui'
import { useVaultStore } from '../stores/vault'
import { useIsMobile } from '../composables/useIsMobile'
import { setLocale } from '../i18n'
import { BRAND } from '../brand'

const { t, locale } = useI18n()
const { isMobile } = useIsMobile()
const COVER_HOW: Record<string, string> = { memo: 'covers.howMemo', calc: 'covers.howCalc', weather: 'covers.howWeather', todo: 'covers.howTodo' }
const ui = useUiStore()
const vault = useVaultStore()
const emit = defineEmits<{ changed: []; close: []; crisis: [] }>()

const curPw = ref(''); const newPw = ref(''); const pwMsg = ref(''); const pwOk = ref(false)
const wipeArm = ref(false)

function save() { ui.saveOpts(); emit('changed') }
function onTheme() { ui.applyTheme(); save() }
function onLang(e: Event) { setLocale((e.target as HTMLSelectElement).value as 'ko' | 'en') }
function onPin(e: Event) { const v = (e.target as HTMLInputElement).value.replace(/\D/g, ''); if (v.length >= 3) ui.opts.pin = v; save() }
async function changePw() {
  pwMsg.value = ''
  if (newPw.value.length < 4) { pwOk.value = false; pwMsg.value = t('settings.pwShort', { n: 4 }); return }
  const ok = await vault.changePassword(curPw.value, newPw.value)
  pwOk.value = ok; pwMsg.value = ok ? t('settings.pwChanged') : t('settings.pwWrong')
  if (ok) { curPw.value = ''; newPw.value = '' }
}
async function wipe() {
  if (!wipeArm.value) { wipeArm.value = true; return }
  await vault.wipeAll(); location.reload()
}
</script>

<template>
  <div class="setp settings">
    <div class="setp-h"><h4>{{ t('settings.settingsTitle') }}</h4><button class="setp-x" @click="emit('close')" aria-label="닫기">×</button></div>

    <div class="setp-sec">{{ t('settings.screen') }}</div>
    <label>{{ t('settings.theme') }}
      <select v-model="ui.opts.theme" @change="onTheme">
        <option value="auto">{{ t('settings.themeAuto') }}</option><option value="light">{{ t('settings.themeLight') }}</option><option value="dark">{{ t('settings.themeDark') }}</option>
      </select></label>
    <label>{{ t('settings.language') }}
      <select :value="locale" @change="onLang"><option value="ko">한국어</option><option value="en">English</option></select></label>
    <label>{{ t('settings.animationOpt') }}
      <select v-model="ui.opts.motion" @change="save">
        <option value="auto">{{ t('settings.animAuto') }}</option><option value="on">{{ t('settings.animOn') }}</option><option value="off">{{ t('settings.animOff') }}</option>
      </select></label>

    <div class="setp-sec">{{ t('settings.security') }}</div>
    <label>{{ t('settings.autoLock') }}
      <select :value="String(ui.opts.autoLockMin)" @change="ui.opts.autoLockMin = +($event.target as HTMLSelectElement).value; save()">
        <option value="1">{{ t('settings.min1') }}</option><option value="5">{{ t('settings.min5') }}</option><option value="15">{{ t('settings.min15') }}</option><option value="30">{{ t('settings.min30') }}</option><option value="0">{{ t('settings.never') }}</option>
      </select></label>
    <label>{{ t('settings.curPw') }}<input type="password" v-model="curPw" autocomplete="off" /></label>
    <label>{{ t('settings.newPw') }}<input type="password" v-model="newPw" autocomplete="off" /></label>
    <button class="setp-btn" @click="changePw">{{ t('settings.change') }}</button>
    <p v-if="pwMsg" class="setp-msg" :class="{ ok: pwOk }">{{ pwMsg }}</p>
    <button class="setp-btn ghost" @click="vault.lock()">{{ t('settings.lockNow') }}</button>

    <div class="setp-sec">{{ t('settings.disguise') }}</div>
    <!-- 폰: 메모장·계산기·날씨·할일 / 웹: 스프레드시트·문서·메일·칸반 -->
    <template v-if="isMobile">
      <label>{{ t('settings.cover') }}
        <select v-model="ui.opts.coverMobile" @change="save">
          <option value="memo">{{ t('covers.memo') }}</option><option value="calc">{{ t('covers.calc') }}</option><option value="weather">{{ t('covers.weather') }}</option><option value="todo">{{ t('covers.todo') }}</option>
        </select></label>
      <p class="setp-hint">{{ t(COVER_HOW[ui.opts.coverMobile]) }}</p>
    </template>
    <label v-else>{{ t('settings.cover') }}
      <select v-model="ui.opts.cover" @change="save">
        <option value="sheet">{{ t('covers.sheet') }}</option><option value="docs">{{ t('covers.docs') }}</option><option value="mail">{{ t('covers.mail') }}</option><option value="kanban">{{ t('covers.kanban') }}</option>
      </select></label>
    <label class="row"><input type="checkbox" v-model="ui.opts.start" @change="save" /> {{ t('settings.startCover') }}</label>
    <label>{{ t('settings.away') }}
      <select v-model="ui.opts.away" @change="save">
        <option value="cover">{{ t('settings.awayCover') }}</option><option value="veil">{{ t('settings.awayVeil') }}</option><option value="none">{{ t('settings.awayNone') }}</option>
      </select></label>
    <label>{{ t('settings.idle') }}
      <select :value="String(ui.opts.idle)" @change="ui.opts.idle = +($event.target as HTMLSelectElement).value; save()">
        <option value="30">{{ t('settings.idle30') }}</option><option value="60">{{ t('settings.idle60') }}</option><option value="300">{{ t('settings.idle300') }}</option><option value="0">{{ t('settings.idle0') }}</option>
      </select></label>
    <label>{{ t('settings.pin') }}<input inputmode="numeric" maxlength="8" :value="ui.opts.pin" @input="onPin" /></label>

    <div class="setp-sec">{{ t('settings.helpSec') }}</div>
    <button class="setp-btn ghost" @click="emit('crisis')">{{ t('settings.crisisOpen') }}</button>

    <div class="setp-sec">{{ t('settings.data') }}</div>
    <button class="setp-btn danger" @click="wipe">{{ wipeArm ? t('settings.wipeConfirm') : t('settings.wipe') }}</button>

    <p class="setp-about">{{ BRAND.appName }} · {{ t('settings.version') }} 0.1</p>
  </div>
</template>
